import { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Modal, ModalActions, ModalFootNote } from '../../components/shared';
import {
  fetchMySignature, saveMySignature, deleteMySignature, mySignatureImageUrl,
  type MySignatureDto, type SignatureKind,
} from '../../api/approval-signature';
import './SignatureModal.css';

/**
 * 내 서명 만들기 — **결재 시트에서 즉석으로** (2026-08-28 · 자리 재정의 판 8).
 *
 * ★★ **다른 화면으로 보내지 않는다.** 결재하려다 서명이 없다고 설정 화면으로 갔다가 돌아오면,
 *    무엇을 하려 했는지 잊는다. 여기서 만들고 그 자리에서 결재한다.
 *
 * ★★ **만드는 법 셋 — 이름으로(기본) · 그려서 · 올려서.**
 *    「이름으로」가 기본인 이유는 **대부분의 사람이 아무것도 안 해도 되게** 하기 위해서다 —
 *    서명은 결재의 조건이 아니고, 등록 안 하면 이름 도장이 찍힌다.
 *
 * ★ **바꿔도 지난 도장은 안 변한다**(서버가 판마다 새 객체에 쓴다) — 화면이 그렇게 말한다.
 */

/**
 * 서명 만드는 법 셋.
 *
 * ★ 이름을 **결과물**로 부른다 (2026-08-29 사용자 지시) — 「이름으로 · 그려서 · 올려서」는
 *   *방법*이라 무엇이 만들어지는지가 안 보였다. 「이름도장 · 자필서명 · 그림서명」은
 *   그 자리에서 무엇이 찍히는지를 말한다.
 */
const TABS: ReadonlyArray<{ key: SignatureKind; label: string; hint: string }> = [
  { key: 'NAME', label: '이름도장', hint: '이름을 도장 모양으로 찍습니다' },
  { key: 'DRAW', label: '자필서명', hint: '마우스·손가락으로 직접 씁니다' },
  { key: 'UPLOAD', label: '그림서명', hint: '스캔한 그림을 올립니다' },
];

const MAX_BYTES = 512 * 1024;
const OK_MIME = new Set(['image/png', 'image/webp']);

interface Props {
  /** 이름 도장 미리보기에 쓸 내 이름. */
  myName: string;
  onClose: () => void;
  /** 저장·삭제가 끝나면 부르는 쪽이 시트를 다시 읽는다. */
  onSaved?: (next: MySignatureDto) => void;
}

export function SignatureModal({ myName, onClose, onSaved }: Props) {
  const [current, setCurrent] = useState<MySignatureDto | null>(null);
  const [tab, setTab] = useState<SignatureKind>('NAME');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** 저장·삭제가 끝났다고 말하는 자리 — 닫지 않으므로 이 한 줄이 유일한 신호다. */
  const [notice, setNotice] = useState<string | null>(null);
  /** 올리거나 그린 그림 — base64(접두사 뗀 것)와 종류. */
  const [picked, setPicked] = useState<{ contentType: string; data: string; preview: string } | null>(null);

  useEffect(() => {
    let alive = true;
    fetchMySignature()
      .then((s) => { if (alive) { setCurrent(s); setTab(s.kind); } })
      // 옛 서버는 이 라우트를 모른다(404) — 그때도 「이름으로」는 고를 수 있어야 하니 기본값으로 둔다.
      .catch(() => { if (alive) setCurrent({ kind: 'NAME', hasImage: false, version: null, updatedAt: null }); });
    return () => { alive = false; };
  }, []);

  const pickFile = useCallback((file: File) => {
    setError(null);
    if (!OK_MIME.has(file.type)) { setError('PNG · WEBP 그림만 올릴 수 있습니다.'); return; }
    if (file.size > MAX_BYTES) { setError('그림은 512KB 이하만 올릴 수 있습니다.'); return; }
    const reader = new FileReader();
    reader.onload = () => {
      const url = String(reader.result ?? '');
      const data = url.slice(url.indexOf(',') + 1);
      setPicked({ contentType: file.type, data, preview: url });
    };
    reader.onerror = () => setError('그림을 읽지 못했습니다.');
    reader.readAsDataURL(file);
  }, []);

  const save = useCallback(async () => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const next = tab === 'NAME'
        ? await saveMySignature({ kind: 'NAME' })
        : await saveMySignature({ kind: tab, contentType: picked!.contentType, data: picked!.data });
      setCurrent(next);
      /**
       * ★★ **저장하고 바로 닫지 않는다** (2026-08-29 · 사용자 지적).
       *    닫아 버리면 «저장이 됐는지» 를 알 길이 없다 — 실제로 저장은 되고 있었는데
       *    화면이 사라지기만 해서 「안 된 것 같다」로 읽혔다. 저장한 결과를 위 «지금 등록된
       *    서명» 에 그대로 보여 주고, 닫는 것은 사람이 정한다.
       * ★ 새로 그린/올린 것은 여기서 버린다 — 이제 서버가 가진 것이 곧 화면의 값이다.
       */
      setPicked(null);
      setNotice('저장했습니다.');
      onSaved?.(next);
    } catch (e) {
      const status = (e as { response?: { status?: number } })?.response?.status;
      setError(status === 413 ? '그림이 너무 큽니다.' : '저장하지 못했습니다.');
    } finally {
      setBusy(false);
    }
  }, [tab, picked, onSaved, onClose]);

  const remove = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const next = await deleteMySignature();
      setCurrent(next);
      setPicked(null);
      setTab('NAME');
      setNotice('서명을 지웠습니다 — 이제 이름 도장이 찍힙니다.');
      onSaved?.(next);
    } catch {
      setError('지우지 못했습니다.');
    } finally {
      setBusy(false);
    }
  }, [onSaved]);

  const canSave = !busy && (tab === 'NAME' || picked != null);

  return (
    <Modal title="내 서명" size="sm" onClose={onClose} footer={
      <>
        <ModalFootNote>
          서명이 없어도 결재는 됩니다 — 그때는 이름 도장이 찍힙니다.
        </ModalFootNote>
        <ModalActions>
          {current?.hasImage && (
            <Button variant="quiet-danger" onClick={() => { void remove(); }} disabled={busy}>
              서명 지우기
            </Button>
          )}
          <Button variant="secondary" onClick={onClose}>닫기</Button>
          <Button onClick={() => { void save(); }} disabled={!canSave}>
            저장
          </Button>
        </ModalActions>
      </>
    }>
      {/*
        ★★ **지금 무엇이 등록돼 있는가** (2026-08-29 · 사용자 지적).
           종전에는 등록된 그림이 «그림서명» 탭에서만 보였다. 그래서 자필서명으로 저장해 두고
           다시 열면 **빈 캔버스**만 보여 「저장이 안 됐다」로 읽혔고, 아래 「서명 지우기」가
           무엇을 지우는 단추인지도 알 수 없었다(같은 탭에 「지우고 다시」가 함께 있다).
        ★ 그래서 **탭과 무관하게** 맨 위에 지금 것을 보여 준다 — 저장 직후에도 여기가 바뀐다.
      */}
      <div className="sgn__now">
        <span className="sgn__nowlabel">지금 등록된 서명</span>
        {current?.hasImage
          ? <img className="sgn__nowimg" src={mySignatureImageUrl(current.version)} alt="지금 등록된 서명" />
          : <NameStamp name={myName} size={42} />}
        <span className="sgn__nowkind">
          {current?.hasImage ? (current.kind === 'DRAW' ? '자필서명' : '그림서명') : '이름도장'}
        </span>
      </div>

      <div className="sgn__tabs" role="group" aria-label="서명 만드는 법">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            className={`sgn__tab${tab === t.key ? ' is-on' : ''}`}
            aria-pressed={tab === t.key}
            onClick={() => { setTab(t.key); setError(null); setNotice(null); }}
          >
            <span className="sgn__tabnm">{t.label}</span>
            <span className="sgn__tabhint">{t.hint}</span>
          </button>
        ))}
      </div>

      {error && <p className="sgn__err" role="alert">{error}</p>}
      {notice && !error && <p className="sgn__ok" role="status">{notice}</p>}

      <div className="sgn__stage">
        {tab === 'NAME' && <NameStamp name={myName} />}
        {tab === 'DRAW' && <DrawPad onDrawn={(p) => setPicked(p)} />}
        {tab === 'UPLOAD' && (
          <UploadPad
            preview={picked?.preview ?? (current?.hasImage ? mySignatureImageUrl(current.version) : null)}
            onPick={pickFile}
          />
        )}
      </div>

      <p className="sgn__note">
        여기서 바꿔도 <strong>이미 찍힌 도장은 안 바뀝니다</strong> — 결재는 「그때 누가 승인했나」의
        기록이라, 지난 문서에는 그때의 도장이 그대로 남습니다.
      </p>
    </Modal>
  );
}

/** 이름 도장 — 그림이 아니라 **글자**다. 서버에 아무것도 안 올린다. */
export function NameStamp({ name, size = 72 }: { name: string; size?: number }) {
  const text = (name || '').trim();
  // 넉 자까지는 그대로, 그보다 길면 뒤 두 자(성 뺀 이름)만 — 도장에 다 넣으면 못 읽는다.
  const shown = text.length <= 4 ? text : text.slice(-2);
  return (
    <span
      className="sgn__namestamp"
      style={{ width: size, height: size, fontSize: Math.round(size / (shown.length > 2 ? 3.4 : 2.6)) }}
      aria-label={`${text} 이름 도장`}
    >
      {shown}
    </span>
  );
}

/** 그려서 — canvas 에 쓰고 PNG 로 만든다. */
function DrawPad({ onDrawn }: { onDrawn: (p: { contentType: string; data: string; preview: string }) => void }) {
  const ref = useRef<HTMLCanvasElement | null>(null);
  const drawing = useRef(false);
  const [dirty, setDirty] = useState(false);

  const pos = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const c = ref.current!;
    const r = c.getBoundingClientRect();
    // ★ 화면 크기와 canvas 픽셀 크기가 달라, 비율로 옮겨야 선이 어긋나지 않는다.
    return { x: (e.clientX - r.left) * (c.width / r.width), y: (e.clientY - r.top) * (c.height / r.height) };
  };

  const start = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const ctx = ref.current?.getContext('2d');
    if (!ctx) return;
    drawing.current = true;
    e.currentTarget.setPointerCapture(e.pointerId);
    const { x, y } = pos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };
  const move = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current) return;
    const ctx = ref.current?.getContext('2d');
    if (!ctx) return;
    const { x, y } = pos(e);
    ctx.lineTo(x, y);
    ctx.lineWidth = 3;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#111';
    ctx.stroke();
    setDirty(true);
  };
  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    const c = ref.current;
    if (!c || !dirty) return;
    const url = c.toDataURL('image/png');
    onDrawn({ contentType: 'image/png', data: url.slice(url.indexOf(',') + 1), preview: url });
  };

  const clear = () => {
    const c = ref.current;
    const ctx = c?.getContext('2d');
    if (!c || !ctx) return;
    ctx.clearRect(0, 0, c.width, c.height);
    setDirty(false);
  };

  return (
    <div className="sgn__draw">
      <canvas
        ref={ref}
        width={520}
        height={180}
        className="sgn__canvas"
        aria-label="서명을 그리는 자리"
        onPointerDown={start}
        onPointerMove={move}
        onPointerUp={end}
        onPointerCancel={end}
      />
      <Button variant="secondary" size="compact" onClick={clear}>지우고 다시</Button>
    </div>
  );
}

/** 올려서 — 스캔한 그림. */
function UploadPad({ preview, onPick }: { preview: string | null; onPick: (f: File) => void }) {
  const ref = useRef<HTMLInputElement | null>(null);
  return (
    <div className="sgn__upload">
      {preview
        ? <img className="sgn__preview" src={preview} alt="올린 서명 미리보기" />
        : <p className="sgn__empty">PNG · WEBP · 512KB 이하</p>}
      <input
        ref={ref}
        type="file"
        accept="image/png,image/webp"
        className="sgn__file"
        aria-label="서명 그림 고르기"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) onPick(f); }}
      />
      <Button variant="secondary" size="compact" onClick={() => ref.current?.click()}>그림 고르기</Button>
    </div>
  );
}
